/**
 * @typedef {Object} ContentProviderOptions
 * @property {string} [cdn] - CDN base URL for content.
 * @property {boolean} [dev] - Whether dev mode is enabled (enables logging).
 * @property {{ info: (obj: object, msg: string) => void }} [logger] - Logger instance.
 * @property {string} [origin] - Origin base URL for content.
 */

/**
 * @typedef {Object} DocumentPropsOg
 * @property {string} [title] - Open Graph title.
 * @property {string} [description] - Open Graph description.
 * @property {string} [image] - Open Graph image URL.
 */

/**
 * @typedef {Object} DocumentProps
 * @property {string} [title] - Page title.
 * @property {string} [description] - Meta description.
 * @property {DocumentPropsOg} [og] - Open Graph overrides.
 */

/**
 * Content entity that may contribute to document props (name, title, summary, meta_info).
 * @typedef {Object} ContentItemWithMeta
 * @property {string} [name]
 * @property {string} [title]
 * @property {string} [summary]
 * @property {{ title?: string, description?: string, og_title?: string, og_description?: string, og_image?: string|{ url: string } }} [meta_info]
 */

/**
 * Indexed response shape (events: byId; trainers: bySlug).
 * @typedef {Object} IndexedContentResponse
 * @property {{ byId?: Record<string, number[]>, bySlug?: Record<string, number[]> }} [index]
 * @property {unknown[]} [data]
 */

/**
 * Same-origin Vercel rewrite to DigitalOcean Spaces (see vercel/output/config.json).
 * Used when corporate firewalls block the CDN host.
 */
const STATIC_API_PROXY_BASE = '/proxy/static-api'

/**
 * @param {unknown} raw
 * @returns {raw is { data: unknown[] }}
 */
function isFeedResponse(raw) {
  return (
    raw != null &&
    typeof raw === 'object' &&
    Array.isArray(/** @type {{ data?: unknown }} */ (raw).data)
  )
}

/**
 * Fetches static (legacy) content from JSON endpoints (CDN or origin).
 */
export class ContentProvider {
  /**
   * @param {ContentProviderOptions} options
   */
  constructor(options) {
    /** @type {string|undefined} */
    this.cdn = options.cdn
    /** @type {boolean|undefined} */
    this.dev = options.dev
    /** @type {{ info: (obj: object, msg: string) => void }|undefined} */
    this.logger = options.logger
    /** @type {string|undefined} */
    this.origin = options.origin
  }

  /**
   * Fetches JSON from a URL. Returns null on network/HTTP failure.
   * @param {string} input
   * @returns {Promise<unknown|null>}
   */
  async fetchJson(input) {
    const { dev, logger } = this
    if (dev && logger) logger.info({ input }, 'Fetch JSON')

    try {
      const resp = await fetch(input)
      if (!resp.ok) return null
      return await resp.json()
    } catch (err) {
      if (dev && logger) logger.info({ input, err }, 'Fetch JSON failed')
      return null
    }
  }

  /**
   * Fetches a feed entity (expects `{ data: [] }`).
   * When `cdn` is true, tries the CDN then falls back to the same-origin
   * `/proxy/static-api` rewrite. When `cdn` is false, fetches origin only.
   * @param {string} entity - Entity name (e.g. 'product-occasions').
   * @param {boolean} [cdn] - If true, use CDN (with proxy fallback); otherwise origin.
   * @returns {Promise<Record<string, unknown>>}
   */
  async findFeedWithProxyFallback(entity, cdn) {
    const { dev, logger } = this
    const primary = `${cdn ? this.cdn : this.origin}/${entity}.json`
    let raw = await this.fetchJson(primary)
    if (isFeedResponse(raw)) return /** @type {Record<string, unknown>} */ (raw)

    // CDN only: browser same-origin proxy when the CDN host is unreachable.
    if (cdn && typeof window !== 'undefined') {
      const proxyUrl = `${STATIC_API_PROXY_BASE}/${entity}.json`
      if (dev && logger) {
        logger.info({ primary, proxyUrl }, 'Feed fetch falling back to proxy')
      }
      raw = await this.fetchJson(proxyUrl)
      if (isFeedResponse(raw)) return /** @type {Record<string, unknown>} */ (raw)
    }

    return {}
  }

  /**
   * Fetches a list of items from an entity JSON (expects response with `data` array).
   * @param {string} entity - Entity name (e.g. 'courses', 'logos').
   * @param {boolean} [cdn] - If true, use CDN URL; otherwise use origin.
   * @returns {Promise<Array<unknown>>}
   */
  async findMany(entity, cdn) {
    const { dev, logger } = this
    const input = `${cdn ? this.cdn : this.origin}/${entity}.json`

    if (dev && logger) logger.info({ input }, 'Fetch findMany')

    const resp = await fetch(input)
    const data = resp.ok ? await resp.json() : {}
    return (data && data.data) || []
  }

  /**
   * Fetches a single entity or entity by id (full JSON response).
   * @param {string} entity - Entity name (e.g. 'events', 'products').
   * @param {string} [id] - Optional id/slug; if empty, fetches entity index.
   * @param {boolean} [cdn] - If true, use CDN URL; otherwise use origin.
   * @returns {Promise<Record<string, unknown>>}
   */
  async findOne(entity, id, cdn) {
    const { dev, logger } = this
    const input = id
      ? `${cdn ? this.cdn : this.origin}/${entity}/${id}.json`
      : `${cdn ? this.cdn : this.origin}/${entity}.json`

    if (dev && logger) logger.info({ input }, 'Fetch findOne')

    const resp = await fetch(input)
    return resp.ok ? await resp.json() : {}
  }

  /**
   * Fetches events index.
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @returns {Promise<Record<string, unknown>>}
   */
  async findEvents(cdn) {
    return this.findOne('events', '', cdn)
  }

  /**
   * Fetches product occasions (optionally test data). Response shape: FindOccasionsResponse (see #root/types/occasions).
   * Falls back to same-origin `/proxy/static-api` when the CDN is blocked.
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @param {boolean} [test] - If true, fetch product-occasions.test.
   * @returns {Promise<import('#root/types/occasions').FindOccasionsResponse>}
   */
  async findOccasions(cdn, test) {
    const raw = await this.findFeedWithProxyFallback(
      test ? 'product-occasions.test' : 'product-occasions',
      cdn
    )
    return /** @type {import('#root/types/occasions').FindOccasionsResponse} */ (
      /** @type {unknown} */ (raw)
    )
  }

  /**
   * Fetches product variants (optionally test data). Response shape: FindVariantsResponse (see #root/types/variants).
   * Falls back to same-origin `/proxy/static-api` when the CDN is blocked.
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @param {boolean} [test] - If true, fetch product-variants.test.
   * @returns {Promise<import('#root/types/variants').FindVariantsResponse>}
   */
  async findVariants(cdn, test) {
    const raw = await this.findFeedWithProxyFallback(
      test ? 'product-variants.test' : 'product-variants',
      cdn
    )
    return /** @type {import('#root/types/variants').FindVariantsResponse} */ (
      /** @type {unknown} */ (raw)
    )
  }

  /**
   * Fetches resources index (`resources.json`).
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @returns {Promise<import('#root/types/resources').FindResourcesResponse>}
   */
  async findResources(cdn) {
    const raw = await this.findOne('resources', '', cdn)
    return /** @type {import('#root/types/resources').FindResourcesResponse} */ (
      /** @type {unknown} */ (raw)
    )
  }

  /**
   * Fetches trainers index.
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @returns {Promise<Record<string, unknown>>}
   */
  async findTrainers(cdn) {
    return this.findOne('trainers', '', cdn)
  }

  /**
   * Fetches a single event by id from the events index.
   * @param {string} id - Event id.
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @returns {Promise<unknown|undefined>}
   */
  async getEventById(id, cdn) {
    const data = /** @type {IndexedContentResponse} */ (
      await this.findOne('events', '', cdn)
    )
    if (
      data &&
      data.index &&
      data.index.byId &&
      Array.isArray(data.index.byId[id])
    ) {
      const ind = data.index.byId[id]
      const idx = ind[0]
      if (ind.length && data.data && idx !== undefined) return data.data[idx]
    }
  }

  /**
   * Fetches a single trainer by slug from the trainers index.
   * @param {string} slug - Trainer slug.
   * @param {boolean} [cdn] - If true, use CDN URL.
   * @returns {Promise<unknown|undefined>}
   */
  async getTrainerBySlug(slug, cdn) {
    const data = /** @type {IndexedContentResponse} */ (
      await this.findOne('trainers', '', cdn)
    )
    if (
      data &&
      data.index &&
      data.index.bySlug &&
      Array.isArray(data.index.bySlug[slug])
    ) {
      const ind = data.index.bySlug[slug]
      const idx = ind[0]
      if (ind.length && data.data && idx !== undefined) return data.data[idx]
    }
  }
}

/**
 * Builds Vike document props (title, description, og) from one or more content items.
 * @param {ContentItemWithMeta|ContentItemWithMeta[]} data - Single object or array of objects with name/title/summary/meta_info.
 * @returns {DocumentProps}
 */
export function toDocumentProps(data) {
  const arr = Array.isArray(data) ? data : [data]
  const props = {}
  const og = {}

  for (const obj of arr) {
    if (typeof obj.name === 'string') props.title = obj.name
    if (typeof obj.title === 'string') props.title = obj.title
    if (typeof obj.summary === 'string') props.description = obj.summary

    if (obj.meta_info) {
      const info = obj.meta_info

      if (typeof info.title === 'string') props.title = info.title
      if (typeof info.description === 'string')
        props.description = info.description

      if (typeof info.og_description === 'string')
        og.description = info.og_description
      if (typeof info.og_title === 'string') og.title = info.og_title
      if (typeof info.og_image === 'string') og.image = info.og_image
      else if (
        info.og_image &&
        typeof info.og_image === 'object' &&
        typeof info.og_image.url === 'string'
      )
        og.image = info.og_image.url
    }
  }

  if (Object.keys(og).length) props.og = og

  return props
}
